import { z } from "zod";
import { ProductSlugSchema } from "@/shared/lib/product-slug";

export const IdParamSchema = z.object({ id: z.string().min(1) });
export const VariantIdParamSchema = z.object({ variantId: z.string().min(1) });

/** คีย์ไอคอนหมวด — เก็บใน DB, map เป็น Phosphor ใน client */
export const CategoryIconKeySchema = z.enum([
  "yarn",
  "scroll",
  "sparkle",
  "scissors",
  "diamond",
  "gift",
  "package",
  "flower",
  "palette",
  "shopping_bag",
]);
export type CategoryIconKey = z.infer<typeof CategoryIconKeySchema>;

export const CategorySchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable().optional(),
  iconKey: CategoryIconKeySchema.nullable().optional(),
  sortOrder: z.number(),
  createdAt: z.coerce.date(),
});
export type CategoryDTO = z.infer<typeof CategorySchema>;

export const CreateCategorySchema = z.object({
  name: z.string().min(1).max(120),
  description: z.string().max(500).optional().nullable(),
  iconKey: CategoryIconKeySchema.optional().nullable(),
  sortOrder: z.number().int().optional(),
});
export type CreateCategoryDTO = z.infer<typeof CreateCategorySchema>;

export const UpdateCategorySchema = z.object({
  name: z.string().min(1).max(120).optional(),
  description: z.string().max(500).optional().nullable(),
  iconKey: CategoryIconKeySchema.optional().nullable(),
  sortOrder: z.number().int().optional(),
});
export type UpdateCategoryDTO = z.infer<typeof UpdateCategorySchema>;

export const VariantInputSchema = z.object({
  id: z.string().optional(),
  name: z.string().min(1).max(120),
  sku: z.string().max(64).optional().nullable(),
  price: z.coerce.number().min(0),
  stockQuantity: z.coerce.number().int().min(0),
  stockUnitLabel: z.string().max(32).optional(),
  receiveUnitLabel: z.string().max(32).optional().nullable(),
  receiveConversionFactor: z.coerce.number().positive().optional(),
  imageKey: z.string().optional().nullable(),
  sortOrder: z.coerce.number().int().optional(),
  isActive: z.boolean().optional(),
  /** คลังรวม — ไม่แสดงใน order picker */
  isInventoryOnly: z.boolean().optional(),
  /** FK ไป pool (ตอนแก้ไข) */
  inventoryVariantId: z.string().optional().nullable(),
  /** index ของ pool ใน array variants (ตอนสร้างใหม่) */
  inventoryVariantIndex: z.coerce.number().int().min(0).optional().nullable(),
  /** หน่วยคลังต่อ 1 หน่วยขาย */
  unitsPerSale: z.coerce.number().positive().optional(),
  /** จำนวนขั้นต่ำก่อนเตือนใกล้หมด — ต่อ variant */
  lowStockThreshold: z.coerce.number().int().min(0).optional(),
  /** simple | bundle (Phase 11) */
  variantType: z.enum(["simple", "bundle"]).optional(),
  /** BOM — ต่อ 1 ชุด (เมื่อ variantType = bundle) */
  bundleLines: z
    .array(
      z.object({
        componentVariantId: z.string().min(1),
        quantity: z.coerce.number().int().min(1),
        sortOrder: z.coerce.number().int().optional(),
      }),
    )
    .optional(),
});

export const ProductCategoryRefSchema = z.object({
  id: z.string(),
  name: z.string(),
  sortOrder: z.number().optional(),
});

/** ข้อมูลสินค้าหลัก — ไม่รวม variants */
export const CreateProductSchema = z.object({
  categoryIds: z.array(z.string().min(1)).min(1),
  name: z.string().min(1).max(200),
  slug: ProductSlugSchema.optional(),
  description: z.string().max(2000).optional().nullable(),
  imageKey: z.string().optional().nullable(),
  isActive: z.boolean().optional(),
  lowStockThreshold: z.coerce.number().int().min(0).optional(),
});
export type CreateProductDTO = z.infer<typeof CreateProductSchema>;

export const UpdateProductSchema = z.object({
  categoryIds: z.array(z.string().min(1)).min(1).optional(),
  name: z.string().min(1).max(200).optional(),
  slug: ProductSlugSchema.optional(),
  description: z.string().max(2000).optional().nullable(),
  imageKey: z.string().optional().nullable(),
  isActive: z.boolean().optional(),
  lowStockThreshold: z.coerce.number().int().min(0).optional(),
});
export type UpdateProductDTO = z.infer<typeof UpdateProductSchema>;

/** จัดการ variants ของสินค้า (sync ทั้งชุด — เพิ่ม/แก้/ลบตาม payload) */
export const SyncProductVariantsSchema = z.object({
  variants: z.array(VariantInputSchema).min(1),
});
export type SyncProductVariantsDTO = z.infer<typeof SyncProductVariantsSchema>;
export type VariantInputDTO = z.infer<typeof VariantInputSchema>;

/** สร้าง/แก้ variant ทีละตัว */
export const CreateVariantSchema = VariantInputSchema;
export type CreateVariantDTO = VariantInputDTO;

export const UpdateVariantSchema = VariantInputSchema;
export type UpdateVariantDTO = VariantInputDTO;

/** ใช้ภายใน seed / duplicate-convert ที่สร้างสินค้า+variants ในทรานแซกชันเดียว */
export type CreateProductWithVariantsDTO = CreateProductDTO & {
  variants: VariantInputDTO[];
};

export const AdjustStockLotPickSchema = z.object({
  stockLotId: z.string().min(1),
  quantity: z.coerce.number().int().positive(),
});

export const AdjustStockSchema = z
  .object({
    quantityDelta: z.coerce.number().int(),
    note: z.string().max(500).optional(),
    lotPicks: z.array(AdjustStockLotPickSchema).optional(),
  })
  .refine(
    (data) =>
      data.quantityDelta !== 0 || (data.lotPicks?.length ?? 0) > 0,
    { message: "quantityDelta zero requires lotPicks" },
  );

export const DuplicateConvertPreviewSchema = z.object({
  sourceVariantId: z.string().min(1),
  sourceQuantityToMove: z.coerce.number().int().min(1),
  targetQuantityPerSource: z.coerce.number().positive(),
});
export type DuplicateConvertPreviewDTO = z.infer<
  typeof DuplicateConvertPreviewSchema
>;

export const DuplicateConvertTargetVariantSchema = z.object({
  name: z.string().min(1).max(120),
  sku: z.string().max(64).optional().nullable(),
  price: z.coerce.number().min(0),
  stockUnitLabel: z.string().max(32).optional(),
  receiveUnitLabel: z.string().max(32).optional().nullable(),
  receiveConversionFactor: z.coerce.number().positive().optional(),
  lowStockThreshold: z.coerce.number().int().min(0).optional(),
});

export const DuplicateConvertVariantSchema = z.object({
  sourceVariantId: z.string().min(1),
  targetVariant: DuplicateConvertTargetVariantSchema,
  moveStock: z.boolean().optional().default(true),
  sourceQuantityToMove: z.coerce.number().int().min(1),
  targetQuantityPerSource: z.coerce.number().positive(),
  deactivateSourceVariant: z.boolean().optional().default(false),
});

export const DuplicateConvertSchema = z.object({
  sourceVariantId: z.string().min(1),
  targetProduct: z.object({
    name: z.string().min(1).max(200),
    categoryIds: z.array(z.string().min(1)).min(1),
    description: z.string().max(2000).optional().nullable(),
    imageKey: z.string().optional().nullable(),
  }),
  targetVariant: DuplicateConvertTargetVariantSchema,
  moveStock: z.boolean().optional().default(true),
  sourceQuantityToMove: z.coerce.number().int().min(1),
  targetQuantityPerSource: z.coerce.number().positive(),
  deactivateSourceVariant: z.boolean().optional().default(false),
  note: z.string().max(500).optional(),
});
export type DuplicateConvertDTO = z.infer<typeof DuplicateConvertSchema>;

export const DuplicateConvertBatchSchema = z.object({
  targetProduct: z.object({
    name: z.string().min(1).max(200),
    categoryIds: z.array(z.string().min(1)).min(1),
    description: z.string().max(2000).optional().nullable(),
    imageKey: z.string().optional().nullable(),
  }),
  variants: z.array(DuplicateConvertVariantSchema).min(1),
  note: z.string().max(500).optional(),
});
export type DuplicateConvertBatchDTO = z.infer<
  typeof DuplicateConvertBatchSchema
>;

export const ProductListQuerySchema = z.object({
  categoryId: z.string().optional(),
  search: z.string().optional(),
  lowStock: z.coerce.boolean().optional(),
  includeInactive: z.coerce.boolean().optional(),
  /** order = ขาย (default), receive = รับเข้า */
  purpose: z.enum(["order", "receive"]).optional(),
  /** ไม่นับจำนวนใน draft order นี้ (เวลาแก้ไขออเดอร์ draft) */
  excludeOrderId: z.string().optional(),
});

export const VariantPriceEditListQuerySchema = ProductListQuerySchema.pick({
  categoryId: true,
  search: true,
  includeInactive: true,
});

export const BulkUpdateVariantPriceItemSchema = z.object({
  variantId: z.string().min(1),
  price: z.coerce.number().min(0),
});

export const BulkUpdateVariantPricesSchema = z.object({
  updates: z.array(BulkUpdateVariantPriceItemSchema).min(1).max(500),
});
export type BulkUpdateVariantPricesDTO = z.infer<
  typeof BulkUpdateVariantPricesSchema
>;
